/**
 * Поиск операционной зоны по ID или внутреннему ID в слое линейных границ
 * Ищет в слое martin.operation_areas_mvt (линейные границы зон)
 *
 * Ищет одновременно по:
 * - id (основной ID)
 * - internal_id (внутренний ID)
 * - number (номер зоны)
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @param {string} zoneId - ID, внутренний ID или номер зоны для поиска
 * @returns {Object|null} { id, internalId, name, region, lat, lon, feature } или null если не найдена
 */
export function searchOperationAreaInTiles (map, zoneId) {
    if (!map || !zoneId) {
        return null;
    }

    const sourceId = 'Операционные зоны_source';
    const sourceLayer = 'martin.operation_areas_mvt';

    try {
        // Проверяем наличие источника на карте
        const source = map.getSource(sourceId);
        if (!source) {
            return null;
        }

        // Получаем features из источника по фильтру ID
        const features = map.querySourceFeatures(sourceId, {
            sourceLayer,
            filter: [
                'any',
                [ '==', [ 'to-string', [ 'get', 'id' ] ], String(zoneId) ],
                [ '==', [ 'to-string', [ 'get', 'internal_id' ] ], String(zoneId) ],
                [ '==', [ 'to-string', [ 'get', 'number' ] ], String(zoneId) ],
            ],
        });

        if (features.length === 0) {
            return null;
        }

        // Берём первый найденный feature (линия)
        const feature = features[0];
        const props = feature.properties;
        const geom = feature.geometry;

        // Вычисляем центр линии (среднее значение координат)
        let lat = null;
        let lon = null;

        if (geom) {
            if (geom.type === 'LineString' && geom.coordinates && geom.coordinates.length > 0) {
                // Берём середину линии
                const midIndex = Math.floor(geom.coordinates.length / 2);
                [ lon, lat ] = geom.coordinates[midIndex];
            } else if (geom.type === 'Polygon' && geom.coordinates && geom.coordinates.length > 0) {
                // На случай если это будет полигон
                const midIndex = Math.floor(geom.coordinates[0].length / 2);
                [ lon, lat ] = geom.coordinates[0][midIndex];
            } else if (geom.type === 'Point' && geom.coordinates) {
                [ lon, lat ] = geom.coordinates;
            }
        }

        return {
            id: props.id || zoneId,
            internalId: props.internal_id || null,
            name: props.name || 'Unnamed Zone',
            number: props.number || null,
            region: props.region || null,
            lat,
            lon,
            feature,
            featureId: props.id,
        };
    } catch (err) {
        return null;
    }
}

/**
 * Получить все операционные зоны из линейного слоя
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @returns {Array} Массив всех зон с основной информацией
 */
export function getAllOperationAreasFromTiles (map) {
    if (!map) return [];

    const sourceId = 'Операционные зоны_source';
    const sourceLayer = 'martin.operation_areas_mvt';

    try {
        const source = map.getSource(sourceId);
        if (!source) return [];

        const features = map.querySourceFeatures(sourceId, {
            sourceLayer,
        });

        return features.map(f => ({
            id: f.properties.id,
            // eslint-disable-next-line camelcase
            internal_id: f.properties.internal_id,
            number: f.properties.number,
            name: f.properties.name,
            region: f.properties.region,
        }));
    } catch (err) {
        return [];
    }
}

/**
 * Применить визуальную подсветку к найденной зоне
 * Меняет цвет линии на красный, увеличивает толщину и opacity
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @param {Object} result - Результат поиска { id, lat, lon, name, region }
 */
export function highlightOperationArea (map, result) {
    if (!map || !result || !result.id) {
        return;
    }

    try {
        const layerId = 'Операционные зоны';
        const zoneId = result.id;

        // Подсветка через dynamic paint properties
        // Красная и толще для найденной зоны, оранжевая нормальная для остальных
        map.setPaintProperty(layerId, 'line-color', [
            'case',
            [ '==', [ 'get', 'id' ], zoneId ],
            '#FF0000',      // Красный для найденной
            '#FF8C00',      // Оранжевый для остальных
        ]);

        map.setPaintProperty(layerId, 'line-width', [
            'case',
            [ '==', [ 'get', 'id' ], zoneId ],
            6,              // Толще для найденной
            3,              // Нормальная для остальных
        ]);

        map.setPaintProperty(layerId, 'line-opacity', [
            'case',
            [ '==', [ 'get', 'id' ], zoneId ],
            1,              // Полная видимость
            0.5,            // Полупрозрачность для остальных
        ]);

        // Делаем пан с зумом к найденной зоне
        if (result.lat && result.lon) {
            map.flyTo({
                center: [ result.lon, result.lat ],
                zoom: 11,
                duration: 1500,
                padding: {
                    top: 100,
                    bottom: 100,
                    left: 100,
                    right: 100,
                },
            });
        }
    } catch (err) {
        // Silently fail
    }
}

/**
 * Очистить подсветку и восстановить оригинальный вид слоя
 *
 * @param {Object} map - Mapbox GL JS map instance
 */
export function clearOperationAreaHighlight (map) {
    if (!map) return;

    try {
        const layerId = 'Операционные зоны';

        // Восстанавливаем оригинальные свойства линии
        map.setPaintProperty(layerId, 'line-color', '#FF8C00');
        map.setPaintProperty(layerId, 'line-width', 3);
        map.setPaintProperty(layerId, 'line-opacity', 0.5);
    } catch (err) {
        // Silently fail
    }
}
